/**
 * Forward Message Modal Component
 * Allows users to forward a message to multiple conversations - WhatsApp style
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, User, Users, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { messagingService } from '@/firebase/messagingService';
import { Conversation } from '@/types/messaging';
import { Message } from '@/types/messaging';
import { userService } from '@/firebase/database';

interface ForwardMessageModalProps {
  message: Message;
  conversations: Conversation[];
  onClose: () => void;
  onForwarded: () => void;
}

interface UserInfo {
  id: string;
  name: string;
  email: string;
  picture?: string;
}

const ForwardMessageModal: React.FC<ForwardMessageModalProps> = ({
  message,
  conversations,
  onClose,
  onForwarded,
}) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversationIds, setSelectedConversationIds] = useState<Set<string>>(new Set());
  const [forwardMessage, setForwardMessage] = useState('');
  const [forwarding, setForwarding] = useState(false);
  const [error, setError] = useState('');
  const [userNamesMap, setUserNamesMap] = useState<Map<string, UserInfo>>(new Map());

  // Fetch user info for participants in direct messages
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!conversations.length) return;

      const userIds = new Set<string>();
      conversations.forEach((conv) => {
        if (conv.type === 'direct') {
          conv.participants?.forEach((id) => {
            if (id !== user?.id) userIds.add(id);
          });
        }
      });

      const infoMap = new Map<string, UserInfo>();
      const fetchPromises = Array.from(userIds).map(async (userId) => {
        try {
          const userResult = await userService.getUser(userId);
          if (userResult.success && userResult.data) {
            infoMap.set(userId, {
              id: userId,
              name: userResult.data.name || userResult.data.email || 'Unknown User',
              email: userResult.data.email || '',
              picture: userResult.data.picture,
            });
          }
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
        }
      });

      await Promise.all(fetchPromises);
      setUserNamesMap(infoMap);
    };

    fetchUserInfo();
  }, [conversations, user?.id]);

  // Filter and sort conversations based on search query
  const filteredConversations = useMemo(() => {
    let filtered = conversations;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = conversations.filter((conv) => {
        if (conv.type === 'group' && conv.name) {
          return conv.name.toLowerCase().includes(query);
        }
        
        // For direct messages, search by participant name
        if (conv.type === 'direct') {
          const otherParticipantId = conv.participants?.find(id => id !== user?.id);
          if (otherParticipantId) {
            const userInfo = userNamesMap.get(otherParticipantId);
            if (userInfo) {
              return userInfo.name.toLowerCase().includes(query) || 
                     userInfo.email.toLowerCase().includes(query);
            }
          }
        }
        
        return false;
      });
    }
    
    // Sort by updatedAt (most recent first), then by createdAt
    return filtered.sort((a, b) => {
      const aUpdated = a.updatedAt?.toDate ? a.updatedAt.toDate() : (a.updatedAt ? new Date(a.updatedAt) : new Date(0));
      const bUpdated = b.updatedAt?.toDate ? b.updatedAt.toDate() : (b.updatedAt ? new Date(b.updatedAt) : new Date(0));
      
      if (bUpdated.getTime() !== aUpdated.getTime()) {
        return bUpdated.getTime() - aUpdated.getTime();
      }
      
      // If updatedAt is same, sort by createdAt
      const aCreated = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
      const bCreated = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
      return bCreated.getTime() - aCreated.getTime();
    });
  }, [conversations, searchQuery, userNamesMap, user?.id]);

  const toggleConversationSelection = (conversationId: string) => {
    setSelectedConversationIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(conversationId)) {
        newSet.delete(conversationId);
      } else {
        newSet.add(conversationId);
      }
      return newSet;
    });
  };

  const handleForward = async () => {
    if (selectedConversationIds.size === 0 || !user?.id || !message.id) {
      setError('Please select at least one conversation');
      return;
    }

    setError('');
    setForwarding(true);

    try {
      const forwardPromises = Array.from(selectedConversationIds).map(async (conversationId) => {
        const result = await messagingService.forwardMessage(
          message.id!,
          conversationId,
          user.id,
          forwardMessage.trim() || undefined
        );
        if (!result.success) {
          throw new Error(result.error || 'Failed to forward message');
        }
        return result;
      });

      await Promise.all(forwardPromises);
      onForwarded();
      onClose();
    } catch (error: any) {
      console.error('Error forwarding message:', error);
      setError(error.message || 'Failed to forward message');
    } finally {
      setForwarding(false);
    }
  };

  // Get conversation display name
  const getConversationDisplayName = (conv: Conversation): string => {
    if (conv.type === 'group' && conv.name) {
      return conv.name;
    }
    
    // For direct messages, get other participant's name
    if (conv.type === 'direct') {
      const otherParticipantId = conv.participants?.find(id => id !== user?.id);
      if (otherParticipantId) {
        const userInfo = userNamesMap.get(otherParticipantId);
        if (userInfo) {
          return userInfo.name;
        }
      }
    }
    
    return 'Direct Message';
  };

  // Get conversation status/description
  const getConversationStatus = (conv: Conversation): string => {
    if (conv.type === 'group') {
      if (conv.description) {
        return conv.description;
      }
      const participantCount = conv.participants?.length || 0;
      return `${participantCount} participant${participantCount !== 1 ? 's' : ''}`;
    }
    
    // For direct messages, show "Message yourself" if it's the user
    if (conv.type === 'direct') {
      const otherParticipantId = conv.participants?.find(id => id !== user?.id);
      if (!otherParticipantId) {
        return 'Message yourself';
      }
      const userInfo = userNamesMap.get(otherParticipantId);
      if (userInfo) {
        return userInfo.email;
      }
    }
    
    return '';
  };

  // Get conversation avatar
  const getConversationAvatar = (conv: Conversation) => {
    if (conv.type === 'group') {
      return null; // Groups don't have single picture
    }
    
    // For direct messages, get other participant's picture
    if (conv.type === 'direct') {
      const otherParticipantId = conv.participants?.find(id => id !== user?.id);
      if (otherParticipantId) {
        const userInfo = userNamesMap.get(otherParticipantId);
        if (userInfo?.picture) {
          return userInfo.picture;
        }
      }
    }
    
    return null;
  };

  // Get message preview text
  const getMessagePreview = (): string => {
    if (message.text) {
      return message.text.length > 50 ? message.text.substring(0, 50) + '...' : message.text;
    }
    if (message.attachments && message.attachments.length > 0) {
      const attachmentTypes = message.attachments.map(a => a.type);
      if (attachmentTypes.includes('image')) return '📷 Image';
      if (attachmentTypes.includes('audio')) return '🎤 Audio';
      return '📎 Attachment';
    }
    return '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md h-[92vh] max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors mr-2"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 flex-1 text-center pr-10">
            Forward message to
          </h2>
        </div>

        {/* Search Bar */}
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name or number"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 text-sm"
            />
          </div>
        </div>

        {/* Recent Chats */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase">Recent chats</h3>
          </div>
          
          {filteredConversations.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-gray-500 text-sm">No conversations found</p>
            </div>
          ) : (
            <div className="px-2">
              {filteredConversations.map((conv) => {
                const isSelected = selectedConversationIds.has(conv.id || '');
                const avatar = getConversationAvatar(conv);
                const displayName = getConversationDisplayName(conv);
                const status = getConversationStatus(conv);

                return (
                  <button
                    key={conv.id}
                    onClick={() => toggleConversationSelection(conv.id || '')}
                    className="w-full flex items-center space-x-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? 'bg-gray-900 border-gray-900'
                        : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>

                    {/* Avatar */}
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={displayName}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                        {conv.type === 'group' ? (
                          <Users className="w-6 h-6 text-gray-500" />
                        ) : (
                          <User className="w-6 h-6 text-gray-500" />
                        )}
                      </div>
                    )}

                    {/* Name and Status */}
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                      {status && (
                        <p className="text-xs text-gray-500 truncate">{status}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-4 py-2 border-t border-gray-200">
            <div className="p-2 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Bottom Bar - Message Preview and Add Message */}
        <div className="border-t border-gray-200 bg-gray-50">
          {/* Message Preview */}
          <div className="px-4 py-2 border-b border-gray-200">
            <div className="flex items-start space-x-3">
              {message.attachments && message.attachments.length > 0 && message.attachments[0].type === 'image' ? (
                <img
                  src={message.attachments[0].url}
                  alt="Preview"
                  className="w-16 h-16 rounded object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">📎</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-1">Forwarding:</p>
                <p className="text-sm text-gray-900 line-clamp-2">{getMessagePreview()}</p>
              </div>
            </div>
          </div>

          {/* Add Message Input */}
          <div className="px-4 py-3">
            <input
              type="text"
              value={forwardMessage}
              onChange={(e) => setForwardMessage(e.target.value)}
              placeholder="Add a message..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 text-sm"
              disabled={forwarding}
            />
          </div>

          {/* Forward Button */}
          <div className="px-4 pb-3">
            <button
              onClick={handleForward}
              disabled={forwarding || selectedConversationIds.size === 0}
              className="w-full py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium text-sm"
            >
              {forwarding ? 'Forwarding...' : `Forward (${selectedConversationIds.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForwardMessageModal;
